import { Component, computed, input } from '@angular/core';
import { BranchTableCellViewModel, BranchTableViewModel } from '../../models/autogenerated-piping';

/** One cell in the compare grid — current and previous code strings plus a changed flag. */
interface BranchCompareCell {
  /** Code string from the current revision (e.g. `"B"`, `"B/W"`). Empty string when the cell is absent in the current version. */
  currentCode: string;
  /** Code string from the previous revision. Empty string when the cell is absent in the previous version. */
  previousCode: string;
  /** Whether the displayed code differs between the two revisions. */
  changed: boolean;
}

/**
 * Reusable compare table for Branch Table cell data.
 *
 * Renders the standard branch-table grid (branch sizes vs header sizes) where changed
 * cells are highlighted — old code struck through in red, new code shown in green.
 *
 * Cells are matched by `(branchSize, headerSize)` coordinate, which is the natural stable
 * key for a branch table entry. The union of all coordinates from both versions is shown,
 * so cells that were added (green) or removed (red) are always visible.
 *
 * Usage:
 * ```html
 * <app-branch-compare-table
 *   [currentBranchTable]="currentTable"
 *   [previousBranchTable]="getPreviousBranchTable(index)">
 * </app-branch-compare-table>
 * ```
 */
@Component({
  selector: 'app-branch-compare-table',
  templateUrl: './branch-compare-table.component.html',
  styleUrls: ['./branch-compare-table.component.scss'],
  standalone: false
})
export class BranchCompareTableComponent {
  /** Branch table from the current revision. */
  currentBranchTable = input<BranchTableViewModel | undefined>(undefined);

  /** Branch table from the previous revision. Pass `undefined` when no previous data is available. */
  previousBranchTable = input<BranchTableViewModel | undefined>(undefined);

  /**
   * Returns the effective previous cells to use for comparison.
   * If the previous table exists but none of its cells carry codes, the API did not
   * return historical code data — we return an empty array to avoid false "new cell" diffs.
   */
  private readonly effectivePreviousCells = computed<BranchTableCellViewModel[]>(() => {
    const cells: BranchTableCellViewModel[] = this.previousBranchTable()?.cells || [];
    const hasCodes: boolean = cells.some(c => c.branchTableCode?.some(code => !!code.code));
    return hasCodes ? cells : [];
  });

  /**
   * Sorted header sizes (descending — largest first) across both revisions.
   * This is the union of all header sizes so that added/removed columns remain visible.
   */
  headerSizes = computed<number[]>(() => {
    const current: BranchTableCellViewModel[] = this.currentBranchTable()?.cells || [];
    const previous: BranchTableCellViewModel[] = this.effectivePreviousCells();
    const allSizes: number[] = [
      ...current.map(c => c.headerSize),
      ...previous.map(c => c.headerSize)
    ].filter((s): s is number => s !== undefined && s !== null);
    return [...new Set(allSizes)].sort((a, b) => b - a);
  });

  /**
   * Sorted branch sizes (descending — largest first) across both revisions.
   * This is the union of all branch sizes so that added/removed rows remain visible.
   */
  branchSizes = computed<number[]>(() => {
    const current: BranchTableCellViewModel[] = this.currentBranchTable()?.cells || [];
    const previous: BranchTableCellViewModel[] = this.effectivePreviousCells();
    const allSizes: number[] = [
      ...current.map(c => c.branchSize),
      ...previous.map(c => c.branchSize)
    ].filter((s): s is number => s !== undefined && s !== null);
    return [...new Set(allSizes)].sort((a, b) => b - a);
  });

  /**
   * 2D compare grid: one entry per `(branchSize, headerSize)` coordinate.
   * Rows correspond to `branchSizes()` (index 0 = largest), columns to `headerSizes()` (index 0 = largest).
   * Each entry carries the current code, previous code, and a changed flag.
   *
   * Guard: if the previous table has cells but every cell is code-less (the API did not return
   * code details for the historical version), the previous cells are treated as absent so that
   * we don't falsely show every current-revision cell as "newly added".
   */
  compareGrid = computed<BranchCompareCell[][]>(() => {
    const currentCells: BranchTableCellViewModel[] = this.currentBranchTable()?.cells || [];
    const previousCells: BranchTableCellViewModel[] = this.effectivePreviousCells();
    const bSizes: number[] = this.branchSizes();
    const hSizes: number[] = this.headerSizes();

    return bSizes.map(branchSize => {
      return hSizes.map(headerSize => {
        const currentCell: BranchTableCellViewModel | undefined =
          currentCells.find(c => c.branchSize === branchSize && c.headerSize === headerSize);
        const previousCell: BranchTableCellViewModel | undefined =
          previousCells.find(c => c.branchSize === branchSize && c.headerSize === headerSize);

        const currentCode: string = this.getCodeFromCell(currentCell);
        const previousCode: string = this.getCodeFromCell(previousCell);

        return { currentCode, previousCode, changed: currentCode !== previousCode };
      });
    });
  });

  /**
   * True when a previous table exists with usable code data and every non-empty cell differs
   * from the current version. Used to render the "entirely replaced" banner above the table.
   *
   * Only non-empty cells (those that have a value in at least one revision) are counted —
   * cells that are absent in both revisions are not considered as "changed".
   */
  isFullyChanged = computed<boolean>(() => {
    // Only show the "entirely replaced" banner when both versions exist.
    // When currentBranchTable is absent the table was simply removed — all cells appear
    // as red strikethrough already; the "replaced" label would be misleading.
    if (!this.currentBranchTable() || !this.previousBranchTable()) { return false; }
    if (!this.effectivePreviousCells().length) { return false; }
    const grid: BranchCompareCell[][] = this.compareGrid();
    const allCells: BranchCompareCell[] = grid.flat();
    const nonEmptyCells: BranchCompareCell[] = allCells.filter(c => c.currentCode || c.previousCode);
    return nonEmptyCells.length > 0 && nonEmptyCells.every(c => c.changed);
  });

  /**
   * Returns the CSS class for a data cell, mirroring the logic in `BranchTableDisplayComponent.getDataCellClass`.
   * Changed cells additionally receive `data-cell-changed` for background tinting.
   * Cells with no value in either revision receive `empty-cell` (white, no border).
   */
  getDataCellClass(cell: BranchCompareCell): string {
    if (!cell.currentCode && !cell.previousCode) { return 'empty-cell'; }
    return cell.changed ? 'data-cell data-cell-changed' : 'data-cell';
  }

  /**
   * Extracts the display code string from a cell by joining all branch table codes with `/`.
   * Codes with a falsy `code` property (null / undefined / empty string) are excluded so they
   * don't contribute spurious empty segments to the joined result.
   * Returns an empty string when the cell is absent or carries no populated codes.
   */
  private getCodeFromCell(cell: BranchTableCellViewModel | undefined): string {
    if (!cell?.branchTableCode?.length) { return ''; }
    const codes: string[] = cell.branchTableCode
      .map(c => c.code)
      .filter((c): c is string => !!c);
    return codes.join('/');
  }
}
